import React from 'react';
import { View, Text, Platform, StyleSheet } from 'react-native';

interface ColorZone {
  threshold: number;  // value at which this color kicks in (upper bound)
  color: string;
}

interface Props {
  value: number;       // 0–100 (drives arc fill and color)
  size?: number;
  label?: string;
  centerText?: string; // overrides the default "{value}%" center display
  colorZones?: ColorZone[];
}

const DEFAULT_ZONES: ColorZone[] = [
  { threshold: 33,  color: '#2E7D32' },
  { threshold: 66,  color: '#F57F17' },
  { threshold: 100, color: '#C62828' },
];

function toRad(deg: number) { return (deg * Math.PI) / 180; }
function pt(cx: number, cy: number, r: number, angleDeg: number) {
  return { x: cx + r * Math.cos(toRad(angleDeg)), y: cy + r * Math.sin(toRad(angleDeg)) };
}
function arcPath(cx: number, cy: number, r: number, fromDeg: number, toDeg: number) {
  const s = pt(cx, cy, r, fromDeg);
  const e = pt(cx, cy, r, toDeg - 0.01);
  const large = toDeg - fromDeg > 180 ? 1 : 0;
  return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
}

export default function GaugeChart({ value, size = 120, label, centerText, colorZones = DEFAULT_ZONES }: Props) {
  const clamped = Math.max(0, Math.min(100, value));
  const displayText = centerText ?? `${clamped}%`;

  if (Platform.OS !== 'web') {
    return (
      <View style={styles.fallback}>
        <Text style={styles.fallbackVal}>{displayText}</Text>
        {label ? <Text style={styles.fallbackLabel}>{label}</Text> : null}
      </View>
    );
  }

  const cx = size / 2;
  const cy = size * 0.6;
  const r = size * 0.38;
  const sw = size * 0.1;
  const START = -210;
  const SPAN  = 240;

  const activeColor = colorZones.find(z => clamped <= z.threshold)?.color ?? colorZones[colorZones.length - 1].color;
  const valueEndAngle = START + (clamped / 100) * SPAN;

  const bgPath  = arcPath(cx, cy, r, START, START + SPAN);
  const valPath = clamped > 0 ? arcPath(cx, cy, r, START, valueEndAngle) : null;

  // Arc endpoints are at ±150° → y = cy + r*sin(150°) = cy + r*0.5
  // Add half stroke-width + 4px margin so the round caps aren't clipped
  const h = Math.ceil(cy + r * 0.5 + sw * 0.5 + 4);

  return (
    <View style={{ alignItems: 'center' }}>
      {/* @ts-ignore — SVG renders fine on RN Web */}
      <svg width={size} height={h} viewBox={`0 0 ${size} ${h}`}>
        {/* @ts-ignore */}
        <path d={bgPath} fill="none" stroke="#E0E0E0" strokeWidth={sw} strokeLinecap="round" />
        {valPath ? (
          // @ts-ignore
          <path d={valPath} fill="none" stroke={activeColor} strokeWidth={sw} strokeLinecap="round" />
        ) : null}
        {/* @ts-ignore */}
        <text x={cx} y={cy + 2} textAnchor="middle" fontSize={size * 0.2} fontWeight="800" fill="#111">
          {displayText}
        </text>
        {label ? (
          // @ts-ignore
          <text x={cx} y={cy + size * 0.18} textAnchor="middle" fontSize={size * 0.1} fill="#888">{label}</text>
        ) : null}
      </svg>
    </View>
  );
}

const styles = StyleSheet.create({
  fallback: { alignItems: 'center', padding: 8 },
  fallbackVal: { fontSize: 24, fontWeight: '800', color: '#111' },
  fallbackLabel: { fontSize: 11, color: '#888', marginTop: 2 },
});
